import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../../errors/errorMessages';
import {
  parseAndValidateTags,
  TAG_MAX_COUNT,
  TAG_MAX_LENGTH,
} from './tagValidation';
import type { PendingTag, TagInputProps } from './types';

type UseTagInputProps = Omit<TagInputProps, 'error' | 'className'>;

function getLastSegment(value: string): string {
  const parts = value.split(',');
  return (parts[parts.length - 1] ?? '').trim();
}

export function useTagInput({
  existingTags,
  newTags,
  onChange,
  disabled = false,
  suggestions = [],
  isSuggestionsLoading = false,
  onSearchChange,
  onPendingChange,
}: UseTagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);
  const [pendingTags, setPendingTags] = useState<PendingTag[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const allTags = useMemo(
    () => [...existingTags, ...newTags],
    [existingTags, newTags]
  );

  const lastSegment = getLastSegment(inputValue);
  const isFloating =
    isFocused || inputValue.length > 0 || pendingTags.length > 0;

  const hasTypedSegment = !isSuggestionsLoading && lastSegment.length > 0;
  const isTypedOnlyOption = hasTypedSegment && suggestions.length === 0;
  const showTypedFirst =
    hasTypedSegment &&
    suggestions.length > 0 &&
    !suggestions.some((s) => s.toLowerCase() === lastSegment.toLowerCase());

  const effectiveItems = useMemo(
    () =>
      isTypedOnlyOption
        ? [lastSegment]
        : showTypedFirst
          ? [lastSegment, ...suggestions]
          : suggestions,
    [isTypedOnlyOption, showTypedFirst, lastSegment, suggestions]
  );

  useEffect(() => {
    setShowSuggestions(lastSegment.length > 0);
    setFocusedSuggestionIndex(-1);
  }, [lastSegment]);

  useEffect(() => {
    onPendingChange?.(inputValue.trim().length > 0 || pendingTags.length > 0);
  }, [inputValue, pendingTags, onPendingChange]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        setDropdownDismissed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = useCallback(() => {
    let typedAdded: string[] = [];
    if (inputValue.trim()) {
      const { added, errors } = parseAndValidateTags(inputValue, [
        ...allTags,
        ...pendingTags.map((t) => t.name),
      ]);
      if (errors.length > 0) {
        setInputError(errors.join(' '));
        return;
      }
      typedAdded = added;
    }

    const totalNew = pendingTags.length + typedAdded.length;
    if (allTags.length + totalNew > TAG_MAX_COUNT) {
      setInputError(QMS_CATEGORY_ERROR_MESSAGES.TAG_MAX_COUNT_REACHED);
      setShowSuggestions(false);
      setDropdownDismissed(true);
      return;
    }

    if (totalNew > 0) {
      const pendingExisting = pendingTags
        .filter((t) => !t.isNew)
        .map((t) => t.name);
      const pendingNew = pendingTags.filter((t) => t.isNew).map((t) => t.name);
      onChange(
        [...existingTags, ...pendingExisting],
        [...newTags, ...pendingNew, ...typedAdded]
      );
    }
    setInputValue('');
    setInputError('');
    setPendingTags([]);
    onSearchChange?.('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [
    inputValue,
    pendingTags,
    existingTags,
    newTags,
    allTags,
    onChange,
    onSearchChange,
  ]);

  const handleToggleSuggestion = useCallback(
    (suggestion: string) => {
      const normalized = suggestion.toLowerCase();
      const isInPending = pendingTags.some(
        (t) => t.name.toLowerCase() === normalized
      );

      if (isInPending) {
        setPendingTags((prev) =>
          prev.filter((t) => t.name.toLowerCase() !== normalized)
        );
        return;
      }

      if (allTags.some((t) => t.toLowerCase() === normalized)) {
        return;
      }

      if (allTags.length + pendingTags.length >= TAG_MAX_COUNT) {
        setInputError(QMS_CATEGORY_ERROR_MESSAGES.TAG_MAX_COUNT_REACHED);
        setShowSuggestions(false);
        setDropdownDismissed(true);
        return;
      }

      setPendingTags((prev) => [...prev, { name: normalized, isNew: false }]);
      setInputValue('');
    },
    [allTags, pendingTags]
  );

  const handleSelectTypedOption = useCallback(() => {
    const normalized = lastSegment.toLowerCase();
    const alreadyPending = pendingTags.find(
      (t) => t.name.toLowerCase() === normalized
    );
    if (alreadyPending) {
      setPendingTags((prev) =>
        prev.filter((t) => t.name.toLowerCase() !== normalized)
      );
      return;
    }

    if (allTags.length + pendingTags.length >= TAG_MAX_COUNT) {
      setInputError(QMS_CATEGORY_ERROR_MESSAGES.TAG_MAX_COUNT_REACHED);
      setShowSuggestions(false);
      setDropdownDismissed(true);
      return;
    }
    const { added, errors } = parseAndValidateTags(lastSegment, [
      ...allTags,
      ...pendingTags.map((t) => t.name),
    ]);
    if (errors.length > 0) {
      setInputError(errors.join(' '));
      return;
    }
    if (added.length === 0) {
      return;
    }
    setPendingTags((prev) => [
      ...prev,
      ...added.map((name) => ({ name, isNew: true })),
    ]);
    const commaIdx = inputValue.lastIndexOf(',');
    const newValue =
      commaIdx >= 0 ? inputValue.slice(0, commaIdx).trimEnd() : '';
    setInputValue(newValue);
    onSearchChange?.(getLastSegment(newValue));
    setShowSuggestions(false);
  }, [lastSegment, inputValue, allTags, pendingTags, onSearchChange]);

  const handleRemovePending = useCallback((tagName: string) => {
    setPendingTags((prev) => prev.filter((t) => t.name !== tagName));
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
        .replace(/[^A-Za-z0-9_-]/g, '')
        .slice(0, TAG_MAX_LENGTH);
      setInputValue(next);
      if (inputError) {
        setInputError('');
      }
      if (next.trim()) {
        setDropdownDismissed(false);
      }
      onSearchChange?.(getLastSegment(next));
    },
    [inputError, onSearchChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSuggestions && effectiveItems.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setFocusedSuggestionIndex((i) =>
            Math.min(i + 1, effectiveItems.length - 1)
          );
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setFocusedSuggestionIndex((i) => Math.max(i - 1, -1));
          return;
        }
        if (e.key === 'Escape') {
          setShowSuggestions(false);
          setDropdownDismissed(true);
          setFocusedSuggestionIndex(-1);
          return;
        }
        if (e.key === 'Enter' && focusedSuggestionIndex >= 0) {
          e.preventDefault();
          const isTypedIdx =
            (isTypedOnlyOption || showTypedFirst) &&
            focusedSuggestionIndex === 0;
          if (isTypedIdx) {
            handleSelectTypedOption();
          } else {
            const suggestionIdx = showTypedFirst
              ? focusedSuggestionIndex - 1
              : focusedSuggestionIndex;
            const focused = suggestions[suggestionIdx];
            if (focused) {
              handleToggleSuggestion(focused);
            }
          }
          return;
        }
        if (e.key === 'Tab' && !e.shiftKey) {
          if (focusedSuggestionIndex < effectiveItems.length - 1) {
            e.preventDefault();
            setFocusedSuggestionIndex((i) => i + 1);
          } else {
            // At last item — close dropdown, let Tab reach the Add button naturally
            setShowSuggestions(false);
            setDropdownDismissed(true);
            setFocusedSuggestionIndex(-1);
          }
          return;
        }
        if (e.key === 'Tab' && e.shiftKey && focusedSuggestionIndex >= 0) {
          e.preventDefault();
          setFocusedSuggestionIndex((i) => Math.max(i - 1, -1));
          return;
        }
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    },
    [
      showSuggestions,
      effectiveItems,
      suggestions,
      focusedSuggestionIndex,
      isTypedOnlyOption,
      showTypedFirst,
      handleAdd,
      handleToggleSuggestion,
      handleSelectTypedOption,
    ]
  );

  const handleRemove = useCallback(
    (tagToRemove: string) => {
      onChange(
        existingTags.filter((t) => t !== tagToRemove),
        newTags.filter((t) => t !== tagToRemove)
      );
    },
    [existingTags, newTags, onChange]
  );

  const isAddDisabled =
    disabled ||
    allTags.length >= TAG_MAX_COUNT ||
    (!inputValue.trim() && pendingTags.length === 0);

  return {
    // refs
    inputRef,
    containerRef,
    // state
    inputValue,
    inputError,
    showSuggestions,
    dropdownDismissed,
    focusedSuggestionIndex,
    setFocusedSuggestionIndex,
    pendingTags,
    isFocused,
    setIsFocused,
    // derived
    allTags,
    lastSegment,
    isFloating,
    isTypedOnlyOption,
    showTypedFirst,
    effectiveItems,
    isAddDisabled,
    // handlers
    handleAdd,
    handleToggleSuggestion,
    handleSelectTypedOption,
    handleRemovePending,
    handleInputChange,
    handleKeyDown,
    handleRemove,
  };
}
